/**
 * AdminBusinessContext — CRUD management for the Business Context Library.
 *
 * Items are reusable GSGO knowledge snippets (mission, programs, differentiators, etc.)
 * that are injected into the AI system prompt when generating blog articles.
 *
 * Features:
 *  - List with title, content preview, order, reorder ↑/↓, edit, delete
 *  - Full-page create/edit form (fixed layout per admin-panel-view-navigation rule)
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  Brain,
  Info,
} from 'lucide-react';
import {
  getAllBusinessContextItems,
  createBusinessContextItem,
  updateBusinessContextItem,
  deleteBusinessContextItem,
  reorderBusinessContextItems,
  seedDefaultGSGOContext,
  type BusinessContextItem,
  type BusinessContextItemPayload,
} from '@/lib/business-context';

interface AdminBusinessContextProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
  titleAction?: React.ReactNode;
}

type View = 'list' | 'create' | 'edit';

interface FormState {
  title: string;
  content: string;
  order: string;
}

const EMPTY_FORM: FormState = { title: '', content: '', order: '0' };

export function AdminBusinessContext({
  currentUserId,
  showNotify,
  confirmAction,
  titleAction,
}: AdminBusinessContextProps): JSX.Element {
  const [items, setItems] = useState<BusinessContextItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<View>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [seeding, setSeeding] = useState(false);

  const notify = useCallback(
    (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
      showNotify?.(msg, type);
    },
    [showNotify]
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllBusinessContextItems();
      setItems(data);
    } catch (err) {
      console.error('[AdminBusinessContext] Failed to load items:', err);
      notify('Failed to load business context items', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validateForm(): boolean {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim()) errors.title = 'Title is required';
    if (!form.content.trim()) errors.content = 'Content is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function itemToForm(item: BusinessContextItem): FormState {
    return {
      title: item.title,
      content: item.content,
      order: String(item.order),
    };
  }

  function formToPayload(): BusinessContextItemPayload {
    return {
      title: form.title.trim(),
      content: form.content.trim(),
      order: parseInt(form.order, 10) || 0,
    };
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  function goToList() {
    setView('list');
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  }

  function openCreate() {
    setForm({ ...EMPTY_FORM, order: String(items.length) });
    setEditingId(null);
    setView('create');
  }

  function openEdit(item: BusinessContextItem) {
    setForm(itemToForm(item));
    setEditingId(item.id);
    setView('edit');
  }

  // ---------------------------------------------------------------------------
  // Save / Delete
  // ---------------------------------------------------------------------------

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = formToPayload();
      if (editingId) {
        await updateBusinessContextItem(editingId, payload, currentUserId);
        notify('Item updated', 'success');
      } else {
        await createBusinessContextItem(payload, currentUserId);
        notify('Item created', 'success');
      }
      await loadItems();
      goToList();
    } catch (err) {
      console.error('[AdminBusinessContext] Save failed:', err);
      notify('Failed to save item', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: BusinessContextItem) {
    confirmAction?.(
      'Delete Context Item',
      `Are you sure you want to delete "${item.title}"? This cannot be undone.`,
      async () => {
        try {
          await deleteBusinessContextItem(item.id);
          notify('Item deleted', 'success');
          await loadItems();
          if (editingId === item.id) goToList();
        } catch (err) {
          console.error('[AdminBusinessContext] Delete failed:', err);
          notify('Failed to delete item', 'error');
        }
      }
    );
  }

  async function handleSeedDefaults() {
    setSeeding(true);
    try {
      await seedDefaultGSGOContext(currentUserId);
      notify('Default GSGO context added or updated. You can edit any item.', 'success');
      await loadItems();
    } catch (err) {
      console.error('[AdminBusinessContext] Seed defaults failed:', err);
      notify('Failed to seed default context', 'error');
    } finally {
      setSeeding(false);
    }
  }

  async function handleMove(item: BusinessContextItem, direction: 'up' | 'down') {
    const idx = items.findIndex((i) => i.id === item.id);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === items.length - 1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newOrder = items.map((i) => i.id);
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];

    try {
      await reorderBusinessContextItems(newOrder, currentUserId);
      await loadItems();
    } catch (err) {
      console.error('[AdminBusinessContext] Reorder failed:', err);
      notify('Failed to reorder items', 'error');
    }
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  if (view === 'list') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Business Context Library</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Knowledge snippets about GSGO automatically injected into the AI when
              generating blog articles.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {titleAction}
            <button
              type="button"
              onClick={handleSeedDefaults}
              disabled={seeding}
              className="inline-flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {seeding ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
              ) : (
                <Brain size={16} />
              )}
              {seeding ? 'Seeding…' : 'Seed default GSGO context'}
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus size={16} />
              Add Context Item
            </button>
          </div>
        </div>

        {/* Info banner */}
        <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-3">
          <Info size={15} className="text-violet-600 shrink-0 mt-0.5" />
          <p className="text-xs text-violet-800">
            These items are concatenated and included in the AI system prompt every time you
            use "Generate with AI" in the blog editor. Add mission statements, key services,
            differentiators, target audiences, tone guidelines, and any other facts the AI
            should always know about GSGO.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-16 text-center">
            <Brain size={40} className="text-gray-300 mb-3" />
            <p className="font-medium text-gray-600">No context items yet</p>
            <p className="text-sm text-gray-400 mt-1 max-w-xs">
              Add knowledge snippets about GSGO so the AI can write better, more accurate
              blog articles.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={handleSeedDefaults}
                disabled={seeding}
                className="inline-flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50"
              >
                {seeding ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
                ) : (
                  <Brain size={15} />
                )}
                {seeding ? 'Seeding…' : 'Seed default GSGO context'}
              </button>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Plus size={15} />
                Add your first item
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div
                key={item.id}
                className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 hover:border-gray-300 transition-colors"
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5 shrink-0 pt-0.5">
                  <button
                    type="button"
                    onClick={() => handleMove(item, 'up')}
                    disabled={idx === 0}
                    className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move up"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(item, 'down')}
                    disabled={idx === items.length - 1}
                    className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move down"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900">{item.title}</p>
                  <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{item.content}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="p-1.5 rounded text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    className="p-1.5 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Create / Edit form view (full-page fixed layout)
  // ---------------------------------------------------------------------------

  const isEditing = view === 'edit';

  return (
    <div className="fixed inset-0 left-0 lg:left-56 z-30 flex flex-col bg-white">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={goToList}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
          >
            <ArrowLeft size={15} />
            Back
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <h2 className="text-base font-semibold text-gray-900 truncate">
            {isEditing ? 'Edit Context Item' : 'New Context Item'}
          </h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isEditing && editingId && (
            <button
              type="button"
              onClick={() => {
                const item = items.find((i) => i.id === editingId);
                if (item) handleDelete(item);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
          >
            {saving && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            {saving ? 'Saving…' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* Form body */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-5">
          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder="e.g. GSGO Mission, Programs, Target Audience"
              className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                formErrors.title ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {formErrors.title && (
              <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.content}
              onChange={(e) => setField('content', e.target.value)}
              placeholder="Write the knowledge snippet the AI should know. Be specific — this is injected verbatim into the AI system prompt."
              rows={12}
              className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm resize-y font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                formErrors.content ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {formErrors.content && (
              <p className="text-xs text-red-500 mt-1">{formErrors.content}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {form.content.length} characters
            </p>
          </div>

          {/* Order */}
          <div className="max-w-[120px]">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
              Order
            </label>
            <input
              type="number"
              min={0}
              value={form.order}
              onChange={(e) => setField('order', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Lower = injected first</p>
          </div>

          {/* Preview */}
          {form.content && (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
              <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-1.5">
                AI prompt preview
              </p>
              <p className="text-xs text-violet-600 mb-2">
                This is how it will appear in the AI system prompt:
              </p>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-white rounded border border-violet-100 p-3">
                {`## ${form.title || '(untitled)'}\n${form.content}`}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
